import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { videoUrl } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isProTier } from '@/lib/tier';
import { ScoreCard } from '@/components/ScoreCard';
import { ScoreRadar } from '@/components/WindCharts';
import { PremiumUpsellCard } from '@/components/PremiumUpsellCard';
import { SharePanel } from '@/components/SharePanel';
import { KaiaCoachVideo } from '@/components/KaiaCoachVideo';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import {
  Loader2, Wind, Share2, AlertTriangle, RefreshCw, Play, Sparkles, FileDown,
  GitCompare, MessageSquare, ChevronRight, Trophy, MapPin, Clapperboard, Star,
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (s) => { const m = Math.floor(s / 60); const sec = Math.floor(s % 60); return `${m}:${sec.toString().padStart(2, '0')}`; };

export default function AnalysisResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const isPro = isProTier(user?.subscription_tier);
  const [a, setA] = useState(null);
  const [err, setErr] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [refBusy, setRefBusy] = useState(false);
  const [refName, setRefName] = useState('');
  const videoRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/analyses/${id}`); setA(data); return data; }
    catch (e) { setErr(t('analysis.notFound')); return null; }
  }, [id, t]);

  useEffect(() => {
    let timer;
    const tick = async () => {
      const data = await load();
      if (data && data.status === 'processing') timer = setTimeout(tick, 3000);
    };
    tick();
    return () => clearTimeout(timer);
  }, [load]);

  const seek = (ts) => {
    if (videoRef.current) {
      videoRef.current.currentTime = ts;
      videoRef.current.play().catch(() => {});
      videoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const toggleReference = async (val) => {
    setRefBusy(true);
    try {
      const { data } = await api.post(`/admin/analyses/${a.id}/reference`, { is_reference: val, name: refName || undefined });
      setA(data);
      toast.success(val ? t('analysis.refMarked') : t('analysis.refRemoved'));
    } catch (e) {
      toast.error(t('analysis.refError'));
    } finally { setRefBusy(false); }
  };

  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/analyses/${a.id}/report.pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `kiteiq-report-${a.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('analysis.pdfDownloaded'));
    } catch (e) {
      toast.error(t('analysis.pdfError'));
    } finally {
      setPdfBusy(false);
    }
  };

  if (err) return <div className="max-w-3xl mx-auto px-6 py-24 text-center"><p className="text-xl">{err}</p><Link to="/analyze" className="text-[#48CAE4] mt-4 inline-block">{t('analysis.analyzeASession')}</Link></div>;
  if (!a) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#00B4D8] animate-spin" /></div>;

  // PROCESSING STATE
  if (a.status === 'processing') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <div className="relative w-28 h-28 mx-auto">
          {a.thumbnail && <img src={a.thumbnail} alt="" className="w-28 h-28 rounded-2xl object-cover opacity-40" />}
          <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-10 h-10 text-[#00B4D8] animate-spin" /></div>
        </div>
        <h1 className="font-display text-4xl mt-8">{t('analysis.processingTitle')}</h1>
        <p className="text-muted-foreground mt-3">{t('analysis.processingDesc')}</p>
        <div className="mt-8 flex justify-center gap-1.5">
          {[0,1,2].map((i) => <span key={i} className="w-2.5 h-2.5 rounded-full bg-[#00B4D8] animate-bounce" style={{ animationDelay: `${i*0.15}s` }} />)}
        </div>
      </div>
    );
  }

  // FAILED STATE
  if (a.status === 'failed') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#FF3B3B]/12 flex items-center justify-center mx-auto"><AlertTriangle className="w-8 h-8 text-[#FF3B3B]" /></div>
        <h1 className="font-display text-4xl mt-6">{t('analysis.failedTitle')}</h1>
        <p className="text-muted-foreground mt-3">{a.error || t('analysis.failedDescDefault')}</p>
        <Button onClick={() => navigate('/analyze')} className="mt-7 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl"><RefreshCw className="w-4 h-4 mr-2" /> {t('analysis.tryAnother')}</Button>
      </div>
    );
  }

  const r = a.result || {};
  const scores = r.scores || {};
  const tricks = r.detected_tricks || [];
  const tips = (r.improvement_tips || []).slice().sort((x, y) => (x.priority || 9) - (y.priority || 9));
  const overall = Object.keys(scores).length ? (Object.values(scores).reduce((p, c) => p + c, 0) / Object.values(scores).length).toFixed(1) : '—';

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
      {/* header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 label-eyebrow text-[#48CAE4] mb-2"><Clapperboard className="w-4 h-4" /> {t('analysis.coachingReport')}</div>
          <h1 className="font-display text-4xl sm:text-5xl capitalize">{r.skill_level_detected || t('analysis.sessionFallback')} {t('analysis.sessionWord')}</h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
            {a.meta?.spot_name && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {a.meta.spot_name}</span>}
            {a.meta?.wind_knots && <span className="flex items-center gap-1"><Wind className="w-4 h-4" /> {a.meta.wind_knots} kn {a.meta.wind_direction || ''}</span>}
            {a.duration_s && <span className="font-data">{fmt(a.duration_s)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="label-eyebrow text-muted-foreground">{t('analysis.overall')}</div>
            <div className="font-data text-4xl font-semibold text-[#00B4D8]">{overall}<span className="text-base text-muted-foreground">/10</span></div>
          </div>
          <SharePanel
            analysisId={a.id}
            shareToken={a.share_token}
            shareEnabled={a.share_enabled}
            onUpdate={(p) => setA((prev) => ({ ...prev, ...p }))}
          />
        </div>
      </div>

      {/* low confidence */}
      {r.low_confidence && (
        <div className="mt-6 rounded-xl border border-[#FFB020]/40 bg-[#FFB020]/5 p-4 flex items-start gap-3" data-testid="low-confidence-alert">
          <AlertTriangle className="w-5 h-5 text-[#FFB020] shrink-0" />
          <div className="text-sm">
            <span className="font-semibold text-[#FFB020]">{t('analysis.lowConfidenceTitle')}</span> {t('analysis.lowConfidenceBody')}
            {r.low_confidence_reasons?.length > 0 && <span className="text-muted-foreground"> {r.low_confidence_reasons.join(' ')}</span>}
            <span className="text-muted-foreground"> {t('analysis.lowConfidenceHint')}</span>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        {/* LEFT: video + summary + tricks */}
        <div className="lg:col-span-2 space-y-6">
          <div className="kite-card overflow-hidden">
            {a.video_available ? (
              <video ref={videoRef} src={videoUrl(a.id)} controls poster={a.thumbnail || undefined} playsInline
                className="w-full aspect-video bg-black" data-testid="analysis-video" />
            ) : (
              <div className="relative w-full aspect-video bg-black flex flex-col items-center justify-center text-muted-foreground overflow-hidden">
                {a.thumbnail && <img src={a.thumbnail} alt="" className="absolute inset-0 opacity-30 w-full h-full object-cover pointer-events-none" />}
                <Play className="w-10 h-10 relative" /><span className="text-sm mt-2 relative">{t('analysis.videoExpired')}</span>
              </div>
            )}
          </div>

          <div className="kite-card p-5">
            <div className="label-eyebrow text-muted-foreground mb-2">{t('analysis.sessionSummary')}</div>
            <p className="text-lg leading-relaxed">{r.session_summary}</p>
          </div>

          {/* Kaia avatar coaching video */}
          <KaiaCoachVideo analysisId={a.id} isPro={isPro} />

          {/* tricks */}
          <div className="kite-card p-5">
            <h3 className="font-semibold flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-[#48CAE4]" /> {t('analysis.detectedTricks')}</h3>
            {tricks.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('analysis.noTricks')}</p>
            ) : (
              <div className="space-y-2">
                {tricks.map((tr, i) => (
                  <button key={i} onClick={() => seek(tr.timestamp_s)} data-testid="analysis-trick-timestamp-button"
                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#0A0A0A] border border-white/8 hover:border-[#48CAE4]/40 transition-colors text-left group">
                    <span className="font-data text-xs px-2 py-1 rounded-md bg-[#00B4D8]/12 text-[#00B4D8]">{fmt(tr.timestamp_s)}</span>
                    <div className="flex-1">
                      <div className="font-medium capitalize">{tr.name}</div>
                      {tr.notes && <div className="text-xs text-muted-foreground">{tr.notes}</div>}
                    </div>
                    <span className="font-data text-sm text-[#48CAE4]">{tr.score}/10</span>
                    <Play className="w-4 h-4 text-muted-foreground group-hover:text-[#48CAE4]" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* observations */}
          {r.observations?.length > 0 && (
            <div className="kite-card p-5">
              <h3 className="font-semibold mb-3">{t('analysis.whatAiSaw')}</h3>
              <ul className="space-y-2">
                {r.observations.map((o, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/85"><ChevronRight className="w-4 h-4 text-[#48CAE4] mt-0.5 shrink-0" /> {o}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* RIGHT: scores + tips + premium */}
        <div className="space-y-6">
          <div className="kite-card p-5">
            <h3 className="font-semibold mb-2">{t('analysis.techniqueScores')}</h3>
            <ScoreRadar scores={scores} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(scores).map(([k, v]) => <ScoreCard key={k} k={k} value={v} />)}
          </div>

          {/* tips */}
          <div className="kite-card p-5">
            <h3 className="font-semibold flex items-center gap-2 mb-3"><Trophy className="w-4 h-4 text-[#FF6B35]" /> {t('analysis.topDrills')}</h3>
            <div className="space-y-3">
              {tips.map((tip, i) => (
                <div key={i} className="p-3 rounded-xl bg-[#0A0A0A] border border-white/8">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-[#00B4D8] text-black font-data text-xs font-bold flex items-center justify-center">{tip.priority}</span>
                    <span className="font-medium text-sm">{tip.tip}</span>
                  </div>
                  {tip.drill_suggestion && <p className="text-xs text-muted-foreground mt-2 pl-8"><span className="text-[#48CAE4]">{t('analysis.drill')}</span> {tip.drill_suggestion}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* encouragement */}
          {r.encouragement && (
            <div className="rounded-2xl p-5" style={{ background: 'radial-gradient(60% 80% at 20% 10%, rgba(0,180,216,0.16), rgba(0,180,216,0)), #12161B', border: '1px solid rgba(0,180,216,0.25)' }}>
              <p className="text-[#48CAE4] font-medium italic">“{r.encouragement}”</p>
            </div>
          )}

          {/* premium upsells / actions */}
          {isPro ? (
            <div className="space-y-3">
              <Button onClick={() => navigate(`/coach/${a.id}`)} className="w-full bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="analysis-ai-chat-btn"><MessageSquare className="w-4 h-4 mr-2" /> {t('analysis.askAiCoach')}</Button>
              <Button onClick={() => navigate(`/compare?left=${a.id}`)} variant="outline" className="w-full border-white/20 bg-white/5 hover:bg-white/10" data-testid="analysis-compare-btn"><GitCompare className="w-4 h-4 mr-2" /> {t('analysis.compareSideBySide')}</Button>
              <Button onClick={exportPdf} disabled={pdfBusy} variant="outline" className="w-full border-white/20 bg-white/5 hover:bg-white/10" data-testid="analysis-export-pdf-btn">{pdfBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} {t('analysis.exportPdf')}</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <PremiumUpsellCard testid="upsell-side-by-side" icon={GitCompare} title={t('analysis.upsellSideTitle')} description={t('analysis.upsellSideDesc')} />
              <PremiumUpsellCard testid="upsell-ai-chat" icon={MessageSquare} title={t('analysis.upsellChatTitle')} description={t('analysis.upsellChatDesc')} />
              <PremiumUpsellCard testid="upsell-pdf" icon={FileDown} title={t('analysis.upsellPdfTitle')} description={t('analysis.upsellPdfDesc')} />
            </div>
          )}

          {user?.is_admin && a.status === 'completed' && (
            <div className="kite-card p-4 mt-3" data-testid="admin-reference-card">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-medium"><Star className="w-4 h-4 text-[#FFB020]" /> {t('analysis.refClip')}</div>
                <Switch checked={!!a.is_reference} disabled={refBusy} onCheckedChange={toggleReference} data-testid="admin-reference-toggle" />
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">{t('analysis.refDesc')}</p>
              {a.is_reference && <p className="text-xs text-[#2EE59D] mt-1.5">{t('analysis.refLiveAs', { name: a.reference_name })}</p>}
              <Input value={refName} onChange={(e) => setRefName(e.target.value)} placeholder={a.reference_name || t('analysis.refNamePlaceholder')} className="mt-2 bg-[#12161B] border-white/15 h-9 text-sm" data-testid="admin-reference-name" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
